import { useCallback, useState } from 'react'

import { RequestStatus } from 'core/domain/Request'

import { type CONTEXTS } from 'legacy/containers/LoadsPage/OfferCard/OfferCard'
import analytics from 'legacy/utils/analytics'
import user from 'legacy/utils/user'

import { type RawRelatedLoadOffer } from '../../domain/RelatedLoad'
import {
  getRelatedLoadOffers,
  type GetRelatedLoadOffersParams,
} from '../../use-cases/related-load/getRelatedLoadOffers'

type State = {
  relatedLoadOffersList: RawRelatedLoadOffer[]
  fetchRelatedLoadOffersStatus: RequestStatus
}

export function useRelatedLoadOffers(context: keyof typeof CONTEXTS) {
  const isUserLoggedIn = user.isLoggedIn()
  const [{ relatedLoadOffersList, fetchRelatedLoadOffersStatus }, setState] = useState<State>({
    relatedLoadOffersList: [],
    fetchRelatedLoadOffersStatus: RequestStatus.Initial,
  })

  const fetchRelatedLoadOffers = useCallback(
    async (params: Omit<GetRelatedLoadOffersParams, 'isUserLoggedIn'>) => {
      try {
        setState({
          relatedLoadOffersList: [],
          fetchRelatedLoadOffersStatus: RequestStatus.Pending,
        })
        const { results, count } = await getRelatedLoadOffers({ ...params, isUserLoggedIn })
        setState({
          relatedLoadOffersList: results,
          fetchRelatedLoadOffersStatus: RequestStatus.Done,
        })
        analytics.track([context, 'Related Loads', 'Fetch Success'], {
          count: count,
        })
      } catch (error) {
        setState({
          relatedLoadOffersList: [],
          fetchRelatedLoadOffersStatus: RequestStatus.Error,
        })
        analytics.track([context, 'Related Loads', 'Fetch Error'], {
          error,
        })
      }
    },
    [context, isUserLoggedIn]
  )

  const isLoading = fetchRelatedLoadOffersStatus === RequestStatus.Pending

  return {
    relatedLoadOffersList,
    isLoading,
    fetchRelatedLoadOffers,
  }
}
